/**
 * Frota · Manutenção · Manutenção Preventiva — reproduz `docs/manutencao/manutenção
 * preventiva.png`: veículos agrupados por placa (a placa aparece UMA vez), cartões
 * de resumo por veículo e, ao expandir, um cartão por item com intervalo, última
 * execução, próxima troca e "Gerar manutenção".
 *
 * A última execução só vale quando alguém a confirma: a referência do ERP aparece
 * ao lado, com "adotar", e nunca vira base sozinha (docs/manutencao/11).
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MaintenancePlanListDto, MaintenancePlanRowDto, MaintenancePlanStatusDto } from "@bi/types";
import { api, ApiError } from "../lib/api";
import { fmtNumber } from "../lib/format";
import { EmptyState } from "../components/EmptyState";
import { IconWrench, IconCalendar, IconChevronDown, IconFilter, IconSearch } from "../components/icons";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import { fmtDay, intervalText } from "../features/maintenance/preventive-ui";
import { groupByPlate, plateSummary, preventiveIconSrc, suggestedForOs, type PlateGroup } from "../features/maintenance/preventive-plate-ui";
import { parseBRNumber } from "../features/maintenance/work-order-ui";
import { Chip, ChipRow, useMobile } from "../features/maintenance-mobile/mobile-ui";

type Faixa = "em_dia" | "proximas" | "vencidas" | "sem_base" | "sem_km";

const BADGE: Record<MaintenancePlanStatusDto, { t: string; c: string }> = {
  em_dia: { t: "Em dia", c: "bg-tm-50 text-tm-700 border-tm-600/20" },
  vencendo: { t: "Próxima", c: "bg-warning-50 text-warning-700 border-warning-600/20" },
  vencido: { t: "Vencida", c: "bg-danger-50 text-danger-700 border-danger-600/20" },
  sem_base: { t: "Sem base", c: "bg-gray-100 text-gray-600 border-gray-300" },
  sem_km: { t: "Sem KM confiável", c: "bg-gray-100 text-gray-600 border-gray-300" },
  km_inconsistente: { t: "KM inconsistente", c: "bg-warning-50 text-warning-700 border-warning-600/20" },
};
const Selo = ({ s }: { s: MaintenancePlanStatusDto }) => (
  <span className={`inline-block whitespace-nowrap rounded-full border px-3 py-0.5 text-xs font-semibold ${BADGE[s].c}`}>{BADGE[s].t}</span>
);

const faixaDe = (s: MaintenancePlanStatusDto): Faixa =>
  s === "em_dia" ? "em_dia" : s === "vencendo" ? "proximas" : s === "vencido" ? "vencidas" : s === "sem_base" ? "sem_base" : "sem_km";

export function MaintenancePreventivePage() {
  const [params, setParams] = useSearchParams();
  const { can } = useCapabilities();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const gerencia = can("workorder.manage");
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ id: string; number: number } | null>(null);
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const mobile = useMobile();

  const faixa = (params.get("faixa") as Faixa | null) ?? "";
  const busca = params.get("plate") ?? "";
  const group = params.get("group") ?? "";

  const lista = useQuery({
    queryKey: ["maintenance", "preventive", group],
    queryFn: () => api.get<MaintenancePlanListDto>(`/maintenance/preventive${group ? `?group=${group}` : ""}`),
  });

  const setParam = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };
  const atualizar = () => { setErro(null); qc.invalidateQueries({ queryKey: ["maintenance"] }); };
  const falha = (msg: string) => (e: unknown) => setErro(e instanceof ApiError ? e.message : msg);

  const todas = useMemo(() => groupByPlate(lista.data?.rows ?? []), [lista.data]);
  const faixaGrupo = (g: PlateGroup) => faixaDe(g.worst);
  const conta = (f: Faixa) => todas.filter((g) => faixaGrupo(g) === f).length;
  const termo = busca.trim().toLowerCase();
  const placas = todas.filter(
    (g) => (!faixa || faixaGrupo(g) === faixa) &&
      (!termo || g.plate.toLowerCase().includes(termo.replace(/[^a-z0-9]/g, "")) || (g.vehicleModel ?? "").toLowerCase().includes(termo)),
  );

  const abrirOS = useMutation({
    mutationFn: (v: { plate: string; codes: string[] }) =>
      api.post<{ id: string; number: number; alreadyOpen?: boolean }>("/maintenance/preventive/work-orders", { plate: v.plate, componentCodes: v.codes }),
    onSuccess: (o) => {
      atualizar();
      if (o.alreadyOpen) setAviso({ id: o.id, number: o.number });
      else navigate(`/frota/manutencao/ordens/${o.id}`);
    },
    onError: falha("Não foi possível abrir a O.S."),
  });

  const expandida = (g: PlateGroup) => abertas.has(g.plate) || (!!busca && placas.length === 1);
  const alternar = (p: string) => setAbertas((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; });

  const Kpi = ({ f, titulo, nota, cor, icone }: { f: Faixa; titulo: string; nota: string; cor: string; icone: string }) => (
    <button
      type="button"
      onClick={() => setParam("faixa", faixa === f ? "" : f)}
      className={`card flex min-h-[6rem] items-center gap-4 p-4 text-left transition-colors ${faixa === f ? "ring-2 ring-tm-500/40" : "hover:bg-surface-muted"}`}
    >
      <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-2xl font-bold ${cor}`} aria-hidden="true">{icone}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-3xl font-bold leading-none text-gray-900">{lista.data ? conta(f) : "—"}</span>
        <span className="block font-semibold text-gray-800">{titulo}</span>
        <span className="block text-xs text-gray-500">{nota}</span>
      </span>
      <span className="text-gray-400" aria-hidden="true">›</span>
    </button>
  );

  return (
    <div className="space-y-4">
      {mobile ? (
        <>
          <label className="card flex items-center gap-2 px-3 py-1.5">
            <IconSearch className="h-5 w-5 shrink-0 text-gray-400" />
            <input className="input w-full !border-0 !px-1 !ring-0" placeholder="Buscar por placa ou modelo" value={busca} onChange={(e) => setParam("plate", e.target.value)} />
          </label>
          <ChipRow>
            <Chip on={!faixa} onClick={() => setParam("faixa", "")}>Todas <span className={!faixa ? "text-white/80" : "text-gray-400"}>{lista.data ? todas.length : ""}</span></Chip>
            {([["vencidas", "Vencidas"], ["proximas", "Próximas"], ["em_dia", "Em dia"], ["sem_base", "Sem base"], ["sem_km", "Sem KM"]] as Array<[Faixa, string]>).map(([f, t]) => (
              <Chip key={f} on={faixa === f} onClick={() => setParam("faixa", faixa === f ? "" : f)}>
                {t} <span className={faixa === f ? "text-white/80" : "text-gray-400"}>{lista.data ? conta(f) : ""}</span>
              </Chip>
            ))}
          </ChipRow>
        </>
      ) : (
      <>
      <header className="flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-xl border border-surface-border bg-white"><IconCalendar className="h-7 w-7 text-gray-800" /></span>
        <div>
          <h2 className="text-4xl font-bold tracking-tight text-gray-900">Manutenção Preventiva</h2>
          <p className="text-gray-500">Veículos agrupados por placa, com linha do tempo clara para execução</p>
        </div>
      </header>

      <section className="grid gap-3 lg:grid-cols-2">
        <label className="card flex items-center gap-3 p-4">
          <IconFilter className="h-7 w-7 text-gray-700" />
          <span className="flex-1">
            <span className="block text-xs text-gray-500">Status</span>
            <select className="input w-full" value={faixa} onChange={(e) => setParam("faixa", e.target.value)}>
              <option value="">Todos os status</option>
              <option value="em_dia">Em dia</option>
              <option value="proximas">Próximas</option>
              <option value="vencidas">Vencidas</option>
              <option value="sem_base">Sem base</option>
              <option value="sem_km">Sem KM confiável</option>
            </select>
          </span>
        </label>
        <label className="card flex items-center gap-3 p-4">
          <IconSearch className="h-7 w-7 text-gray-700" />
          <span className="flex-1">
            <span className="block text-xs text-gray-500">Buscar por placa ou modelo</span>
            <input className="input w-full" placeholder="Digite a placa, modelo ou descrição…" value={busca} onChange={(e) => setParam("plate", e.target.value)} />
          </span>
        </label>
      </section>

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Kpi f="em_dia" titulo="Em dia" nota="Veículos com manutenções em dia" cor="bg-tm-50 text-tm-600" icone="✓" />
        <Kpi f="proximas" titulo="Próximas" nota="Itens perto do vencimento" cor="bg-warning-50 text-warning-600" icone="◷" />
        <Kpi f="sem_base" titulo="Sem base" nota="Sem execução registrada" cor="bg-orange-50 text-orange-600" icone="!" />
        <Kpi f="vencidas" titulo="Vencidas" nota="Manutenções em atraso" cor="bg-danger-50 text-danger-600" icone="!" />
        <Kpi f="sem_km" titulo="Sem KM confiável" nota="Veículos sem leitura de KM" cor="bg-gray-100 text-gray-600" icone="?" />
      </section>
      </>
      )}

      {erro && <div className="card border-danger-600/30 bg-danger-50 p-3 text-sm text-danger-600">{erro}</div>}
      {aviso && (
        <div className="card flex flex-wrap items-center gap-3 border-warning-600/30 bg-warning-50 p-3 text-sm text-warning-700">
          <span>Já existe uma O.S. aberta para esta preventiva.</span>
          <Link className="btn-secondary" to={`/frota/manutencao/ordens/${aviso.id}`}>Abrir a O.S. #{aviso.number}</Link>
        </div>
      )}

      {lista.isLoading ? (
        <div className="card p-6 text-sm text-gray-500">Carregando…</div>
      ) : lista.isError ? (
        <div className="card p-6 text-sm text-danger-600">Não foi possível carregar a preventiva. Tente novamente.</div>
      ) : placas.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<IconWrench className="w-5 h-5" />}
            title={todas.length === 0 ? "Plano preventivo ainda não configurado" : "Nenhum veículo com este filtro"}
            description={todas.length === 0 ? "Monte o plano nos Grupos de veículos: cada placa do grupo recebe os itens." : undefined}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {placas.map((g) => (
            <PlacaCard
              key={g.plate} g={g} gerencia={gerencia} aberta={expandida(g)} onAlternar={() => alternar(g.plate)}
              abrindo={abrirOS.isPending}
              onAbrirOS={(codes) => abrirOS.mutate({ plate: g.plate, codes })}
              onMudou={atualizar} onErro={falha("Não foi possível salvar.")}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PlacaCard({
  g, gerencia, aberta, onAlternar, abrindo, onAbrirOS, onMudou, onErro,
}: {
  g: PlateGroup; gerencia: boolean; aberta: boolean; onAlternar: () => void; abrindo: boolean;
  onAbrirOS: (codes: string[]) => void; onMudou: () => void; onErro: (e: unknown) => void;
}) {
  const resumo = plateSummary(g.rows);
  const sugeridos = suggestedForOs(g.rows);
  const km = g.currentKm;
  const status: MaintenancePlanStatusDto = g.worst === "km_inconsistente" ? "sem_km" : g.worst;
  return (
    <section className="card overflow-hidden">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 p-4">
        <button type="button" onClick={onAlternar} aria-expanded={aberta} className="flex min-h-[4rem] min-w-0 flex-1 basis-full items-center gap-4 text-left sm:min-w-[18.75rem] sm:basis-auto">
          <img src="/manutencao-bi/t-1.png" alt="" className="h-14 w-20 flex-shrink-0 rounded-lg object-cover" />
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-3">
              <span className="text-2xl font-bold tracking-tight text-gray-900">{g.plate}</span>
              <Selo s={status} />
            </span>
            <span className="mt-0.5 flex flex-wrap items-center gap-2 text-gray-600">
              {g.vehicleModel ?? (g.isTrailer ? "Carreta" : "Cavalo")}
              {g.group && <span className="rounded-md bg-surface-muted px-2 py-0.5 text-xs text-gray-600">Grupo: {g.group.name}</span>}
            </span>
          </span>
        </button>

        <div className="flex items-center gap-3">
          <span className="text-2xl text-gray-500" aria-hidden="true">◔</span>
          <span>
            <span className="block text-xs text-gray-500">KM atual</span>
            <span className="block text-xl font-bold text-gray-900">{km ? `${fmtNumber(km.km)} km` : "—"}</span>
            {g.kmStale && <span className="block text-xs text-warning-700">KM possivelmente desatualizada</span>}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-2xl text-gray-500" aria-hidden="true">▤</span>
          <span>
            <span className="block text-xs text-gray-500">Itens preventivos</span>
            <span className="block text-xl font-bold text-gray-900">{g.rows.length} {g.rows.length === 1 ? "item" : "itens"}</span>
          </span>
        </div>
        <div className="hidden text-xs text-gray-500 xl:block">
          {[resumo.vencido ? `${resumo.vencido} vencida(s)` : null, resumo.proximo ? `${resumo.proximo} próxima(s)` : null, resumo.emDia ? `${resumo.emDia} em dia` : null]
            .filter(Boolean).join(" · ")}
        </div>
        {/* No celular vira faixa larga com texto: alvo de polegar, e diz o que faz. */}
        <button type="button" onClick={onAlternar} aria-label={aberta ? "Recolher" : "Expandir"}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-surface-border text-gray-600 sm:w-11">
          <IconChevronDown className={`h-5 w-5 transition-transform ${aberta ? "rotate-180" : ""}`} />
          <span className="text-sm font-medium sm:hidden">{aberta ? "Recolher itens" : "Ver itens"}</span>
        </button>
      </div>

      {aberta && (
        <div className="border-t border-surface-border p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xl font-semibold text-gray-900">Itens de manutenção preventiva</h3>
            <div className="flex flex-wrap gap-2">
              {gerencia && sugeridos.length > 1 && (
                <button className="btn-primary min-h-[2.5rem] text-sm" disabled={abrindo} onClick={() => onAbrirOS(sugeridos.map((r) => r.componentCode))}>
                  Abrir O.S. ({sugeridos.length} itens)
                </button>
              )}
              <Link className="btn-secondary min-h-[2.5rem] text-sm" to={`/frota/placa/${g.plate}`}>Ver histórico completo</Link>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
            {g.rows.map((r) => (
              <ItemCard key={r.id} r={r} gerencia={gerencia} onAbrirOS={() => onAbrirOS([r.componentCode])} onMudou={onMudou} onErro={onErro} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function ItemCard({
  r, gerencia, onAbrirOS, onMudou, onErro,
}: { r: MaintenancePlanRowDto; gerencia: boolean; onAbrirOS: () => void; onMudou: () => void; onErro: (e: unknown) => void }) {
  const [modo, setModo] = useState<null | "registrar" | "intervalo">(null);
  const s = r.status;
  const adotar = useMutation({
    mutationFn: () => api.post(`/maintenance/preventive/items/${r.id}/adopt-erp`),
    onSuccess: onMudou,
    onError: onErro,
  });
  const icone = preventiveIconSrc(r.label, "cartao");
  const proxima = [s.nextKm != null ? `${fmtNumber(s.nextKm)} km` : null, s.nextDate ? fmtDay(s.nextDate) : null].filter(Boolean).join(" · ");
  return (
    <article className="card flex gap-3 p-4">
      {icone ? <img src={icone} alt="" className="h-12 w-12 flex-shrink-0 object-contain mix-blend-multiply" /> : <span className="h-12 w-12 shrink-0" />}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-semibold leading-tight text-gray-900">{r.label}</h4>
          <Selo s={s.status} />
        </div>
        <dl className="mt-2 space-y-0.5 text-sm text-gray-600">
          <div><span className="font-medium text-gray-700">Intervalo:</span> {intervalText(r).replace("a cada ", "")}</div>
          <div>
            <span className="font-medium text-gray-700">Última execução:</span>{" "}
            {r.lastExecution
              ? `${r.lastExecution.odometerKm != null ? `${fmtNumber(r.lastExecution.odometerKm)} km ` : ""}(${fmtDay(r.lastExecution.executedAt)})`
              : "sem base"}
            {!r.lastExecution && r.erpReference && (
              <span className="text-xs text-gray-500">
                {" "}· ERP: {fmtNumber(r.erpReference.km)} km ({fmtDay(r.erpReference.date)})
                {gerencia && <button className="ml-1 text-tm-600 hover:underline disabled:opacity-50" disabled={adotar.isPending} onClick={() => adotar.mutate()}>adotar</button>}
              </span>
            )}
          </div>
          <div><span className="font-medium text-gray-700">Próxima troca:</span> {proxima || "—"}</div>
        </dl>
        {r.details && r.details.length > 0 && (
          <p className="mt-1 text-xs text-gray-500">{r.details.slice(0, 3).map((d) => `${d.label}: ${d.value}${d.unit ? ` ${d.unit}` : ""}`).join(" · ")}</p>
        )}
        {gerencia && (
          <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
            <button className="text-xs text-gray-500 hover:underline" onClick={() => setModo(modo === "registrar" ? null : "registrar")}>Registrar</button>
            <button className="text-xs text-gray-500 hover:underline" onClick={() => setModo(modo === "intervalo" ? null : "intervalo")}>Intervalo</button>
            {r.openWorkOrder ? (
              <Link className="inline-flex min-h-[2.5rem] items-center rounded-lg border border-info-600/30 bg-info-50 px-3 text-sm font-medium text-info-700" to={`/frota/manutencao/ordens/${r.openWorkOrder.id}`}>
                O.S. #{r.openWorkOrder.number}
              </Link>
            ) : (
              <button className="inline-flex min-h-[2.5rem] items-center gap-1.5 rounded-lg bg-tm-600 px-3.5 text-sm font-semibold text-white hover:bg-tm-700" onClick={onAbrirOS}>
                🔧 Gerar manutenção
              </button>
            )}
          </div>
        )}
        {modo && (
          <div className="mt-2 rounded-lg bg-surface-muted p-3">
            {modo === "registrar"
              ? <RegistrarExecucao r={r} onFeito={() => { setModo(null); onMudou(); }} onErro={onErro} />
              : <EditarIntervalo r={r} onFeito={() => { setModo(null); onMudou(); }} onErro={onErro} />}
          </div>
        )}
      </div>
    </article>
  );
}

function RegistrarExecucao({ r, onFeito, onErro }: { r: MaintenancePlanRowDto; onFeito: () => void; onErro: (e: unknown) => void }) {
  const [data, setData] = useState(() => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" }));
  const [km, setKm] = useState(r.currentKm && !r.isTrailer ? String(r.currentKm.km) : "");
  const [obs, setObs] = useState("");
  const exigeKm = !!r.intervalKm;
  const kmN = useMemo(() => parseBRNumber(km), [km]);

  const salvar = useMutation({
    mutationFn: () =>
      api.post("/maintenance/preventive/executions", {
        plate: r.plate,
        componentCode: r.componentCode,
        executedAt: new Date(`${data}T12:00:00-03:00`).toISOString(),
        odometerKm: kmN != null ? Math.round(kmN) : null,
        notes: obs.trim() || null,
      }),
    onSuccess: onFeito,
    onError: onErro,
  });

  return (
    <div className="flex flex-wrap items-end gap-2">
      <span className="text-sm text-gray-700 mr-2">Registrar <strong>{r.label}</strong> em {r.plate}:</span>
      <label className="block">
        <span className="text-xs text-gray-500">Data</span>
        <input type="date" className="input mt-0.5" value={data} onChange={(e) => setData(e.target.value)} />
      </label>
      <label className="block">
        <span className="text-xs text-gray-500">KM{exigeKm ? " *" : ""}</span>
        <input className="input mt-0.5 w-36" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} />
      </label>
      <label className="block flex-1 min-w-[12.5rem]">
        <span className="text-xs text-gray-500">Observação</span>
        <input className="input mt-0.5 w-full" value={obs} maxLength={1000} placeholder="Ex.: feita na concessionária, NF 1234" onChange={(e) => setObs(e.target.value)} />
      </label>
      <button className="btn-primary" disabled={salvar.isPending || !data || (exigeKm && kmN == null)} onClick={() => salvar.mutate()}>
        Registrar
      </button>
    </div>
  );
}

function EditarIntervalo({ r, onFeito, onErro }: { r: MaintenancePlanRowDto; onFeito: () => void; onErro: (e: unknown) => void }) {
  const [km, setKm] = useState(r.intervalKm ? String(r.intervalKm) : "");
  const [dias, setDias] = useState(r.intervalDays ? String(r.intervalDays) : "");
  const kmN = parseBRNumber(km);
  const diasN = parseBRNumber(dias);

  const salvar = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/maintenance/preventive/items/${r.id}`, body),
    onSuccess: onFeito,
    onError: onErro,
  });

  return (
    <div className="flex flex-wrap items-end gap-2">
      <span className="text-sm text-gray-700 mr-2">Intervalo de <strong>{r.label}</strong> em {r.plate}:</span>
      <label className="block">
        <span className="text-xs text-gray-500">KM</span>
        <input className="input mt-0.5 w-36" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} />
      </label>
      <label className="block">
        <span className="text-xs text-gray-500">Dias</span>
        <input className="input mt-0.5 w-24" inputMode="numeric" value={dias} onChange={(e) => setDias(e.target.value)} />
      </label>
      <button
        className="btn-primary"
        disabled={salvar.isPending || (kmN == null && diasN == null)}
        onClick={() => salvar.mutate({ intervalKm: kmN != null ? Math.round(kmN) : null, intervalDays: diasN != null ? Math.round(diasN) : null })}
      >
        Salvar
      </button>
      <button className="btn-ghost text-danger-600" disabled={salvar.isPending} onClick={() => salvar.mutate({ active: false })}>
        Tirar do plano
      </button>
      <p className="w-full text-xs text-gray-500">
        Ajustado aqui, o intervalo deixa de acompanhar o ERP{r.source === "erp" ? " (hoje vem de lá)" : ""}.
      </p>
    </div>
  );
}
